import { useCallback, useEffect, useMemo } from 'react';
import { X, ArrowRight, Info, Palette, Type, Sliders, Volume2, Image, Layout, Sparkles, Crosshair } from 'lucide-react';
import { cn } from '@/utils/cn';

// ─── Types ─────────────────────────────────────────────

export interface VariantDetailData {
  id: string;
  name: string | null;
  variantLabel: string | null;
  description: string | null;
  status: string;
  quality: { score?: number } | null;
  fileSize: number | null;
  previewUrl: string | null;
  variantPatch?: Record<string, any> | null;
  sourceConfig?: Record<string, any> | null;
  createdAt: string;
}

interface VariantDetailModalProps {
  open: boolean;
  variant: VariantDetailData | null;
  onClose: () => void;
}

// ─── Path → 中文维度名映射 ─────────────────────────────

const PATH_LABELS: Record<string, string> = {
  // 主题 & 视觉
  'meta.theme': '主题名称',
  'meta.themeLabel': '主题标签',
  'meta.visualStyle': '美术风格',
  'meta.variantId': '变体标识',
  'meta.variantLabel': '变体标签',
  themeColors: '元素配色',
  themeBg: '背景渐变色',
  // CTA
  'cta.buttonStyle': 'CTA 按钮样式',
  'cta.buttonText': 'CTA 按钮文案',
  'cta.buttonColor': 'CTA 按钮颜色',
  'cta.position': 'CTA 位置',
  'cta.trigger': 'CTA 触发时机',
  'cta.triggerValue': 'CTA 触发值',
  'cta.frequency': 'CTA 频率',
  'cta.transition': 'CTA 过渡动效',
  // UI
  'ui.buttonStyle': 'UI 按钮样式',
  'ui.progressStyle': '进度条样式',
  'ui.overlayStyle': '覆盖层样式',
  'ui.scoreDisplay': '分数展示样式',
  'ui.livesDisplay': '生命展示样式',
  // 难度 & 关卡
  difficulty: '难度',
  'board.cols': '棋盘列数',
  'board.rows': '棋盘行数',
  targetValue: '目标分数',
  maxLevel: '最大关卡数',
  mergeMode: '合并模式',
  mergeInitialFill: '初始填充率',
  newTileChance: '新方块概率',
  // 音效
  'audio.enabled': '音效开关',
  'audio.bgmStyle': '背景音乐风格',
  'audio.volume': '音量',
  // 本地化
  'localization.cta_btn': 'CTA 按钮文案',
  'localization.start_btn': '开始按钮文案',
  'localization.retry_btn': '重试按钮文案',
  'localization.win_title': '胜利标题',
  'localization.lose_title': '失败标题',
  // 图片槽位
  imageSlots: '图片素材替换',
  // 其他
  variantId: '变体 ID',
  duration: '试玩时长',
  locale: '语言',
};

// CTA position 中文映射
const CTA_POSITION_MAP: Record<string, string> = {
  bottom_bar: '底部横条',
  center_overlay: '居中覆盖',
  top_banner: '顶部横幅',
  full_screen_overlay: '全屏覆盖',
};

// ─── Helpers ────────────────────────────────────────────

/** 获取维度中文标签 */
function getLabel(path: string): string {
  return PATH_LABELS[path] ?? path;
}

/** 获取维度图标 */
function getIcon(path: string) {
  if (path.includes('theme') || path.includes('Color') || path.includes('Bg') || path.includes('color')) return Palette;
  if (path.includes('text') || path.includes('btn') || path.includes('title') || path.includes('localization') || path.includes('locale')) return Type;
  if (path.includes('audio') || path.includes('bgm') || path.includes('volume')) return Volume2;
  if (path.includes('imageSlots')) return Image;
  if (path.includes('cta') || path.includes('button') || path.includes('overlay')) return Layout;
  if (path.includes('difficulty') || path.includes('board') || path.includes('target') || path.includes('level') || path.includes('merge') || path.includes('duration')) return Sliders;
  if (path.includes('effect') || path.includes('particle')) return Sparkles;
  return Crosshair;
}

/** 扁平化对象为路径数组 */
function flattenPaths(obj: Record<string, any>, prefix = ''): Array<{ path: string; value: any }> {
  const result: Array<{ path: string; value: any }> = [];
  for (const [key, value] of Object.entries(obj)) {
    const fullPath = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !Array.isArray(value) && key !== 'imageSlots') {
      result.push(...flattenPaths(value, fullPath));
    } else {
      result.push({ path: fullPath, value });
    }
  }
  return result;
}

/** 从嵌套对象中按路径取值 */
function getByPath(obj: Record<string, any> | null | undefined, path: string): { found: boolean; value: any } {
  if (!obj) return { found: false, value: undefined };
  const parts = path.split('.');
  let current: any = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return { found: false, value: undefined };
    if (!(part in current)) return { found: false, value: undefined };
    current = current[part];
  }
  return { found: true, value: current };
}

/** 格式化展示值 */
function formatValue(value: any): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? '开启' : '关闭';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    // 颜色数组特殊展示
    if (value.every((v) => typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v))) {
      return value.join(', ');
    }
    return JSON.stringify(value);
  }
  return JSON.stringify(value);
}

/** 判断值是否为颜色值 */
function isColorValue(value: any): boolean {
  if (typeof value === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(value)) return true;
  if (Array.isArray(value) && value.length >= 2 && value.every((v) => typeof v === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(v))) return true;
  return false;
}

/** 颜色色块渲染 */
function ColorSwatch({ value }: { value: any }) {
  const colors: string[] = Array.isArray(value) ? value : [value];
  return (
    <span className="inline-flex items-center gap-1">
      {colors.map((c, i) => (
        <span
          key={i}
          className="inline-block h-4 w-4 rounded border border-border/40 shadow-sm"
          style={{ backgroundColor: c }}
          title={c}
        />
      ))}
      <span className="text-[10px] text-muted-foreground">{colors.join(', ')}</span>
    </span>
  );
}

/** imageSlots 展示 */
function ImageSlotList({ slots }: { slots: Record<string, string> }) {
  const entries = Object.entries(slots);
  if (entries.length === 0) return <span className="text-muted-foreground">-</span>;
  return (
    <div className="flex flex-col gap-1">
      {entries.map(([key, prompt]) => (
        <div key={key} className="flex items-start gap-1.5 text-xs">
          <code className="shrink-0 rounded bg-muted/40 px-1 py-0.5 text-[10px] text-neon-cyan">{key}</code>
          <span className="text-foreground/80">{prompt}</span>
        </div>
      ))}
    </div>
  );
}

// ─── CTA position 值映射 ────────────────────────────────

function displayValue(path: string, value: any): string {
  if (path === 'cta.position' || path.endsWith('ctaPosition')) {
    return CTA_POSITION_MAP[value] ?? value;
  }
  return formatValue(value);
}

// ─── Component ──────────────────────────────────────────

export function VariantDetailModal({ open, variant, onClose }: VariantDetailModalProps): JSX.Element | null {
  // ESC 关闭
  const handleKeyDown = useCallback(
    (e: DocumentEventMap['keydown']) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleKeyDown]);

  // 解析 patch 条目
  const patchEntries = useMemo(() => {
    if (!variant?.variantPatch) return [];
    return flattenPaths(variant.variantPatch);
  }, [variant?.variantPatch]);

  if (!open || !variant) return null;

  const vLabel = variant.variantLabel || variant.name || '未命名变体';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="glass-card flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border/20 px-5 py-4">
          <div className="flex items-center gap-2">
            <Info size={16} className="text-neon-cyan" />
            <h3 className="text-base font-semibold text-foreground">变体详情</h3>
          </div>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:text-foreground transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* 基本信息 */}
          <div className="mb-4 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground w-16 shrink-0">变体名称</span>
              <span className="text-sm font-medium text-foreground">{vLabel}</span>
            </div>
            {variant.name && variant.name !== vLabel && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground w-16 shrink-0">内部名</span>
                <code className="rounded bg-muted/40 px-1.5 py-0.5 text-[11px] text-neon-cyan">{variant.name}</code>
              </div>
            )}
            {variant.description && (
              <div className="flex items-start gap-2">
                <span className="text-xs font-medium text-muted-foreground w-16 shrink-0 pt-0.5">描述</span>
                <span className="text-xs text-foreground/80 leading-relaxed">{variant.description}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground w-16 shrink-0">创建时间</span>
              <span className="text-xs text-muted-foreground">
                {new Date(variant.createdAt).toLocaleString('zh-CN')}
              </span>
            </div>
            {variant.quality?.score != null && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground w-16 shrink-0">质量分</span>
                <span className="text-xs text-foreground/80">{variant.quality.score} 分</span>
              </div>
            )}
          </div>

          {/* 变更维度表格 */}
          {patchEntries.length > 0 ? (
            <div>
              <h4 className="mb-2.5 text-xs font-semibold text-foreground/90 flex items-center gap-1.5">
                <Sparkles size={12} className="text-neon-purple" />
                变更维度（{patchEntries.length} 项）
              </h4>
              <div className="rounded-lg border border-border/20 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border/20 bg-muted/10">
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground w-[40%]">维度</th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground w-[30%]">原始值</th>
                      <th className="px-3 py-2 text-center font-medium text-muted-foreground w-[30px]" />
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground w-[30%]">变体值</th>
                    </tr>
                  </thead>
                  <tbody>
                    {patchEntries.map(({ path, value }) => {
                      const Icon = getIcon(path);
                      const source = variant.sourceConfig ? getByPath(variant.sourceConfig, path) : { found: false, value: undefined };
                      const isImageSlot = path === 'imageSlots' && value && typeof value === 'object';

                      return (
                        <tr key={path} className="border-b border-border/10 last:border-b-0 hover:bg-muted/5 transition-colors">
                          <td className="px-3 py-2.5 align-top">
                            <span className="inline-flex items-center gap-1.5 text-foreground/90">
                              <Icon size={11} className="shrink-0 text-muted-foreground" />
                              <span className="font-medium">{getLabel(path)}</span>
                            </span>
                            {getLabel(path) !== path && (
                              <span className="mt-0.5 block text-[9px] text-muted-foreground/60">{path}</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 align-top text-foreground/70">
                            {source.found ? (
                              isColorValue(source.value) ? (
                                <ColorSwatch value={source.value} />
                              ) : (
                                <span className="break-all">{displayValue(path, source.value)}</span>
                              )
                            ) : (
                              <span className="text-muted-foreground/50 italic">-</span>
                            )}
                          </td>
                          <td className="px-1 py-2.5 align-top text-center">
                            <ArrowRight size={11} className="text-neon-cyan/60" />
                          </td>
                          <td className="px-3 py-2.5 align-top text-foreground/90">
                            {isImageSlot ? (
                              <ImageSlotList slots={value} />
                            ) : isColorValue(value) ? (
                              <ColorSwatch value={value} />
                            ) : (
                              <span className={cn('break-all', source.found && formatValue(source.value) !== formatValue(value) ? 'text-neon-green font-medium' : '')}>
                                {displayValue(path, value)}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
              <Info size={20} className="opacity-40" />
              <p className="text-xs">该变体无详细的维度变更记录</p>
              <p className="text-[10px] text-muted-foreground/60">可能是历史数据或 AI 自动生成模式</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end border-t border-border/20 px-5 py-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-muted transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
}
